import { Injectable } from "@angular/core";
import {
    GrupoAcceso,
    GrupoAccesoModulo,
} from "../models/seguridad/grupo_acceso.model";
import { WHATSAPP_CONVERSATION_ACCESS } from "../models/notificacion/whatsapp_conversation.model";
import { SeguridadService } from "./seguridad.service";

/**
 * Texto visible de cada opcion del menu, en un solo lugar.
 *
 * Por que existe: cada item se arma DOS veces —una rama para `accesoTotal` y
 * otra para permisos granulares—. Mientras el texto estuvo duplicado a mano,
 * once opciones terminaron con redacciones distintas segun el permiso del
 * usuario ("Tarjetas de Credito" sin tilde, "Consulta de Extractos " con un
 * espacio de mas, "Procesar Pagare" en singular). Con una constante por item
 * las dos ramas no pueden divergir.
 */
const ETIQUETAS = {
    ACTUALIZAR_EXTRACTOS: "Actualizar Extractos",
    ACTUALIZAR_SALDO_GRUPO_TARJETA: "Actualizar Saldo Grupo Tarjeta",
    ACTUALIZAR_SALDO_TARJETA: "Actualizar Saldo Tarjeta",
    ASIENTOS: "Asientos",
    ASOCIACIONES: "Asociaciones",
    CAJERO: "Cajero",
    COBROS_POR_RECUPERADOR: "Cobros por Recuperador",
    CONCEPTOS: "Conceptos",
    CONFIGURACION: "Configuración",
    CONSULTA_DE_EXTRACTOS: "Consulta de Extractos",
    CONSULTA_DE_TARJETAS_DE_CREDITO: "Consulta de tarjetas de credito",
    CONTABILIDAD: "Contabilidad",
    CONTACTOS: "Contactos",
    CRM: "CRM",
    CUENTAS: "Cuentas",
    EMPRESAS: "Empresas",
    ENVIAR_EXTRACTO: "Enviar Extracto",
    ENVIAR_NOTIFICACION_POR_CORREO: "Enviar Notificacion por Correo",
    ENVIAR_NOTIFICACION_POR_MENSAJE: "Enviar Notificacion por Mensaje",
    ESTADO_DE_CUENTA: "Estado de cuenta",
    ESTADO_DE_PAGARES: "Estado de Pagares",
    EXTRACTOS_ENVIADOS: "Extractos Enviados",
    GESTION_COBROS: "Gestion Cobros",
    GESTION_DE_CARTERA: "Gestion de Cartera",
    GRAFICOS: "Graficos",
    GRUPOS_DE_ACCESO: "Grupos de Acceso",
    GRUPOS_DE_TARJETAS: "Grupos de Tarjetas",
    HISTORIAL_NOTIFICACIONES: "Historial Notificaciones",
    HOME: "Home",
    IMPORTAR_CONTACTO: "Importar Contacto",
    IMPORTAR_TARJETAS: "Importar Tarjetas",
    INFORME_CONSULTA_DE_EXTRACTOS: "Informe Consulta de Extractos",
    INFORME_DE_FACTURACION: "Informe de Facturacion",
    INFORME_DE_LLAMADAS: "Informe de llamadas",
    INFORME_DE_NOTIFICACIONES: "Informe de Notificaciones",
    INFORME_DE_PAGARES: "Informe de Pagares",
    INFORME_DE_RENDICIONES: "Informe de Rendiciones",
    INFORME_DE_RENDICIONES_CAJA: "Informe de Rendiciones Caja",
    INFORME_DE_TARJETAS: "Informe de Tarjetas",
    INFORME_DE_VENDEDORES: "Informe de Vendedores",
    INFORME_EXTRACTOS_ENVIADOS: "Informe Extractos Enviados",
    INFORME_PRESTAMOS: "Informe Prestamos",
    INICIO: "Inicio",
    NOTIFICACION: "Notificacion",
    PAGARES: "Pagares",
    FLUJOS_DE_WHATSAPP: "Flujos de WhatsApp",
    PLANTILLAS_DE_WHATSAPP: "Plantillas de WhatsApp",
    PRESTAMO: "Prestamo",
    PRESTAMO_SOLIDARIO: "Prestamo Solidario",
    PRESTAMOS: "Prestamos",
    PROCESAR_PAGARES: "Procesar Pagares",
    PROCESOS: "Procesos",
    RENDICIONES: "Rendiciones",
    RENDICIONES_DE_CAJA: "Rendiciones de Caja",
    REPORTES: "Reportes",
    SEGUIMIENTO: "Seguimiento",
    SEGUIMIENTO_DE_PRESTAMOS: "Seguimiento de Prestamos",
    SEGURIDAD: "Seguridad",
    SOLICITUD_DE_CREDITO: "Solicitud de Credito",
    SUCURSALES: "Sucursales",
    TARJETA: "Tarjeta",
    TARJETAS_DE_CREDITO: "Tarjetas de Crédito",
    TARJETAS_PREPAGA: "Tarjetas Prepaga",
    TESORERIA: "Tesoreria",
    USUARIOS: "Usuarios",
    VENDEDORES: "Vendedores",
    VERIFICACION_DE_CORREOS: "Verificacion de Correos",
};
// Sin `as const` a proposito: congelar los tipos literales haria que
// `menu.push(...)` fallara, porque el array queda tipado con la etiqueta del
// primer elemento ("Home"). Las claves se siguen validando igual.

@Injectable({
    providedIn: "root",
})
export class MenuService {
    currentUser: any;
    grupoAcceso: GrupoAcceso;
    routerLinkBase: string = "/pages";
    linkPagoFacilSolicitudes: string = "";

    constructor(public seguridad: SeguridadService) {}

    async ngOnInit(): Promise<void> {}

    async getMenu() {
        this.currentUser = this.seguridad.getCurrentUser();
        this.grupoAcceso = new GrupoAcceso();
        this.linkPagoFacilSolicitudes =
            await this.seguridad.getLinkPagoFacilSoliciitudes();
        await this.grupoAcceso.cargarObjeto(this.currentUser.grupo_acceso);
        // console.log(this.grupoAcceso);

        const menuRouterLink = `${this.routerLinkBase}/home`;
        const menu = [
            {
                label: ETIQUETAS.HOME,
                icon: "pi pi-fw pi-home",
                routerLink: [menuRouterLink],
                items: [
                    {
                        label: ETIQUETAS.INICIO,
                        icon: "pi pi-fw pi-th-large",
                        routerLink: [`${menuRouterLink}/inicio`],
                    },
                    {
                        label: ETIQUETAS.GRAFICOS,
                        icon: "pi pi-fw pi-chart-bar",
                        routerLink: [`${menuRouterLink}/grafico`],
                    },
                ],
            },
        ];

        if (this.grupoAcceso.accesoTotal) {
            menu.push(
                await this.getMenuConfiguracion(this.grupoAcceso.accesoTotal)
            );
            menu.push(
                await this.getMenuContabilidad(this.grupoAcceso.accesoTotal)
            );
            menu.push(
                await this.getMenuTesoreria(this.grupoAcceso.accesoTotal)
            );
            menu.push(
                await this.getMenuClienteTarjeta(this.grupoAcceso.accesoTotal)
            );
            menu.push(await this.getMenuPrestamo(this.grupoAcceso.accesoTotal));
            menu.push(
                await this.getMenuPrestamoSolidario(
                    this.grupoAcceso.accesoTotal
                )
            );
            menu.push(
                await this.getMenuNotificacion(this.grupoAcceso.accesoTotal)
            );
            menu.push(await this.getMenuCRM(this.grupoAcceso.accesoTotal));
            menu.push(
                await this.getMenuSeguimiento(this.grupoAcceso.accesoTotal)
            );
            menu.push(
                await this.getMenuAsociaciones(this.grupoAcceso.accesoTotal)
            );
            menu.push(
                await this.getMenuSeguridad(this.grupoAcceso.accesoTotal)
            );
        } else {
            // modulos activos
            for (const modulo of this.grupoAcceso.modulos) {
                const mod = new GrupoAccesoModulo();
                mod.cargarObjeto(modulo);
                if (this.shouldIncludeModuleMenu(mod.activo)) {
                    switch (mod.codigo) {
                        case "configuracion":
                            menu.push(
                                await this.getMenuConfiguracion(mod.accesoTotal)
                            );
                            break;
                        case "contabilidad":
                            menu.push(
                                await this.getMenuContabilidad(mod.accesoTotal)
                            );
                            break;
                        case "cliente_tarjeta":
                            menu.push(
                                await this.getMenuClienteTarjeta(
                                    mod.accesoTotal
                                )
                            );
                            break;
                        case "prestamo_solidario":
                            menu.push(
                                await this.getMenuPrestamoSolidario(
                                    mod.accesoTotal
                                )
                            );
                            break;
                        case "prestamo":
                            menu.push(
                                await this.getMenuPrestamo(mod.accesoTotal)
                            );
                            break;
                        case "tesoreria":
                            menu.push(
                                await this.getMenuTesoreria(mod.accesoTotal)
                            );
                            break;
                        case "notificacion":
                            menu.push(
                                await this.getMenuNotificacion(mod.accesoTotal)
                            );
                            break;
                        case "crm":
                            menu.push(await this.getMenuCRM(mod.accesoTotal));
                            break;
                        case "asociaciones":
                            menu.push(
                                await this.getMenuAsociaciones(mod.accesoTotal)
                            );
                            break;
                        case "seguridad":
                            menu.push(
                                await this.getMenuSeguridad(mod.accesoTotal)
                            );
                            break;
                        case "seguimiento":
                            menu.push(
                                await this.getMenuSeguimiento(mod.accesoTotal)
                            );
                            break;
                        default:
                            break;
                    }
                }
            }

        }
        return menu;
    }

    private shouldIncludeModuleMenu(moduloActivo: boolean): boolean {
        return moduloActivo === true;
    }

    async getMenuConfiguracion(accesoTotal: boolean) {
        const menuRouterLink = `${this.routerLinkBase}/configuracion`;
        const menu = {
            label: ETIQUETAS.CONFIGURACION,
            icon: "pi pi-fw pi-cog",
            routerLink: [menuRouterLink],
            codigo: "configuracion",
            items: [],
        };
        if (accesoTotal) {
            menu.items.push({
                label: ETIQUETAS.REPORTES,
                icon: "pi pi-fw pi-book",
                items: [
                    {
                        label: ETIQUETAS.INFORME_DE_VENDEDORES,
                        icon: "pi pi-fw pi-id-card",
                        routerLink: [
                            `${menuRouterLink}/reportes/informe-vendedor`,
                        ],
                    },
                ],
            });
            menu.items.push(
                ...[
                    {
                        label: ETIQUETAS.EMPRESAS,
                        icon: "pi pi-fw pi-building",
                        routerLink: [`${menuRouterLink}/empresas`],
                    },
                    {
                        label: ETIQUETAS.SUCURSALES,
                        icon: "pi pi-fw pi-sitemap",
                        routerLink: [`${menuRouterLink}/sucursales`],
                    },
                    {
                        label: ETIQUETAS.VENDEDORES,
                        icon: "pi pi-fw pi-users",
                        routerLink: [`${menuRouterLink}/vendedores`],
                    },
                    // {label: 'Grupos de Clientes', icon: this.icon, routerLink: [`${menuRouterLink}/grupos-clientes`]},
                ]
            );
        } else {
            // Reportes
            const reportes = [];
            for (const reporte of this.grupoAcceso.reportes.filter(
                (rep) => rep.modulo === "configuracion" && rep.activo === true
            )) {
                switch (reporte.codigo) {
                    case "informe_vendedor":
                        reportes.push({
                            label: ETIQUETAS.INFORME_DE_VENDEDORES,
                            icon: "pi pi-fw pi-id-card",
                            routerLink: [
                                `${menuRouterLink}/reportes/informe-vendedor`,
                            ],
                        });
                        break;
                }
            }
            if (reportes.length)
                menu.items.push({
                    label: ETIQUETAS.REPORTES,
                    icon: "pi pi-fw pi-book",
                    items: reportes,
                });
            // REGISTROS
            for (const registro of this.grupoAcceso.registros.filter(
                (reg) => reg.modulo === "configuracion"
            )) {
                switch (registro.codigo) {
                    case "empresa":
                        if (registro.ver) {
                            menu.items.push({
                                label: ETIQUETAS.EMPRESAS,
                                icon: "pi pi-fw pi-building",
                                routerLink: [`${menuRouterLink}/empresas`],
                            });
                        }
                        break;
                    case "sucursal":
                        if (registro.ver) {
                            menu.items.push({
                                label: ETIQUETAS.SUCURSALES,
                                icon: "pi pi-fw pi-sitemap",
                                routerLink: [`${menuRouterLink}/sucursales`],
                            });
                        }
                        break;
                    case "vendedor":
                        if (registro.ver) {
                            menu.items.push({
                                label: ETIQUETAS.VENDEDORES,
                                icon: "pi pi-fw pi-users",
                                routerLink: [`${menuRouterLink}/vendedores`],
                            });
                        }
                        break;
                    // case 'grupo_cliente':
                    //     if ( registro.ver ) {
                    //         menu.items.push( {label: 'Grupos de Clientes', icon: this.icon, routerLink: [`${menuRouterLink}/grupos-clientes`]} )
                    //     }
                    // break;
                    default:
                        break;
                }
            }
        }
        return menu;
    }

    async getMenuSeguridad(accesoTotal: boolean) {
        const menuRouterLink = `${this.routerLinkBase}/seguridad`;
        const menu = {
            label: ETIQUETAS.SEGURIDAD,
            icon: "pi pi-fw pi-desktop",
            routerLink: [menuRouterLink],
            codigo: "seguridad",
            items: [],
        };
        if (accesoTotal) {
            menu.items = [
                {
                    label: ETIQUETAS.USUARIOS,
                    icon: "pi pi-fw pi-user",
                    routerLink: [`${menuRouterLink}/usuarios`],
                },
                {
                    label: ETIQUETAS.GRUPOS_DE_ACCESO,
                    icon: "pi pi-fw pi-shield",
                    routerLink: [`${menuRouterLink}/grupos-acceso`],
                },
            ];
        } else {
            for (const registro of this.grupoAcceso.registros.filter(
                (reg) => reg.modulo === "seguridad"
            )) {
                switch (registro.codigo) {
                    case "usuario":
                        if (registro.ver) {
                            menu.items.push({
                                label: ETIQUETAS.USUARIOS,
                                icon: "pi pi-fw pi-user",
                                routerLink: [`${menuRouterLink}/usuarios`],
                            });
                        }
                        break;
                    case "grupo_acceso":
                        if (registro.ver) {
                            menu.items.push({
                                label: ETIQUETAS.GRUPOS_DE_ACCESO,
                                icon: "pi pi-fw pi-shield",
                                routerLink: [`${menuRouterLink}/grupos-acceso`],
                            });
                        }
                        break;
                    default:
                        break;
                }
            }
        }

        return menu;
    }

    async getMenuNotificacion(accesoTotal: boolean) {
        const menuRouterLink = `${this.routerLinkBase}/notificacion`;
        const menu = {
            label: ETIQUETAS.NOTIFICACION,
            icon: "pi pi-fw pi-send",
            routerLink: [menuRouterLink],
            codigo: "notificacion",
            items: [],
        };
        if (accesoTotal) {
            menu.items.push({
                label: ETIQUETAS.REPORTES,
                icon: "pi pi-fw pi-book",
                items: [
                    {
                        label: ETIQUETAS.INFORME_DE_NOTIFICACIONES,
                        icon: "pi pi-fw pi-chart-bar",
                        routerLink: [
                            `${menuRouterLink}/reportes/informe-notificacion`,
                        ],
                    },
                ],
            }),
                menu.items.push({
                    label: ETIQUETAS.PROCESOS,
                    icon: "pi pi-fw pi-cog",
                    items: [
                        {
                            label: ETIQUETAS.ENVIAR_NOTIFICACION_POR_CORREO,
                            icon: "pi pi-fw pi-envelope",
                            routerLink: [
                                `${menuRouterLink}/procesos/enviar-correo`,
                            ],
                        },
                        {
                            label: ETIQUETAS.IMPORTAR_CONTACTO,
                            icon: "pi pi-fw pi-upload",
                            routerLink: [
                                `${menuRouterLink}/procesos/procesar-contacto`,
                            ],
                        },
                        {
                            label: ETIQUETAS.ENVIAR_NOTIFICACION_POR_MENSAJE,
                            icon: "pi pi-fw pi-comment",
                            routerLink: [
                                `${menuRouterLink}/procesos/enviar-mensaje`,
                            ],
                        },
                    ],
                }),
                menu.items.push(
                    ...[
                        {
                            label: ETIQUETAS.CONTACTOS,
                            icon: "pi pi-fw pi-users",
                            routerLink: [`${menuRouterLink}/contactos`],
                        },
                        {
                            label: ETIQUETAS.HISTORIAL_NOTIFICACIONES,
                            icon: "pi pi-fw pi-history",
                            routerLink: [
                                `${menuRouterLink}/historial-notificaciones`,
                            ],
                        },
                        {
                            label: WHATSAPP_CONVERSATION_ACCESS.NOMBRE_PLURAL,
                            icon: "pi pi-fw pi-comments",
                            routerLink: [
                                `${menuRouterLink}/whatsapp-inbox`,
                            ],
                        },
                        {
                            label: ETIQUETAS.PLANTILLAS_DE_WHATSAPP,
                            icon: "pi pi-fw pi-file",
                            routerLink: [
                                `${menuRouterLink}/whatsapp-templates`,
                            ],
                        },
                    ]
                );
        } else {
            const reportes = [];
            for (const reporte of this.grupoAcceso.reportes.filter(
                (rep) => rep.modulo === "notificacion" && rep.activo === true
            )) {
                switch (reporte.codigo) {
                    case "informe_notificacion":
                        reportes.push({
                            label: ETIQUETAS.INFORME_DE_NOTIFICACIONES,
                            icon: "pi pi-fw pi-chart-bar",
                            routerLink: [
                                `${menuRouterLink}/reportes/informe-notificacion`,
                            ],
                        });
                        break;
                }
            }
            if (reportes.length)
                menu.items.push({
                    label: ETIQUETAS.REPORTES,
                    icon: "pi pi-fw pi-book",
                    items: reportes,
                });
            //procesos
            const procesos = [];
            for (const permiso of this.grupoAcceso.permisos.filter((rep) => rep.activo === true)) {
                switch (permiso.codigo) {
                    case "enviar_notificacion_correo":
                        procesos.push({
                            label: ETIQUETAS.ENVIAR_NOTIFICACION_POR_CORREO,
                            icon: "pi pi-fw pi-envelope",
                            routerLink: [
                                `${menuRouterLink}/procesos/enviar-correo`,
                            ],
                        });
                        break;
                    case "importar_contacto":
                        procesos.push({
                            label: ETIQUETAS.IMPORTAR_CONTACTO,
                            icon: "pi pi-fw pi-upload",
                            routerLink: [
                                `${menuRouterLink}/procesos/procesar-contacto`,
                            ],
                        });
                        break;
                    case "enviar_notificacion_mensaje":
                        procesos.push({
                            label: ETIQUETAS.ENVIAR_NOTIFICACION_POR_MENSAJE,
                            icon: "pi pi-fw pi-comment",
                            routerLink: [
                                `${menuRouterLink}/procesos/enviar-mensaje`,
                            ],
                        });
                        break;
                }
            }
            if (procesos.length)
                menu.items.push({
                    label: ETIQUETAS.PROCESOS,
                    icon: "pi pi-fw pi-cog",
                    items: procesos,
                });
            for (const registro of this.grupoAcceso.registros.filter((reg) => reg.modulo === "notificacion")) {
                switch (registro.codigo) {
                    case "contacto":
                        if (registro.ver) {
                            menu.items.push({
                                label: ETIQUETAS.CONTACTOS,
                                icon: "pi pi-fw pi-users",
                                routerLink: [`${menuRouterLink}/contactos`],
                            });
                        }
                        break;
                    case "historial_notificacion":
                        if (registro.ver) {
                            menu.items.push({
                                label: ETIQUETAS.HISTORIAL_NOTIFICACIONES,
                                icon: "pi pi-fw pi-history",
                                routerLink: [
                                    `${menuRouterLink}/historial-notificaciones`,
                                ],
                            });
                        }
                        break;
                    case "whatsapp_template":
                        if (registro.ver) {
                            menu.items.push({
                                label: ETIQUETAS.PLANTILLAS_DE_WHATSAPP,
                                icon: "pi pi-fw pi-file",
                                routerLink: [
                                    `${menuRouterLink}/whatsapp-templates`,
                                ],
                            });
                        }
                        break;
                    case WHATSAPP_CONVERSATION_ACCESS.CODIGO_REGISTRO:
                        if (registro.ver) {
                            menu.items.push({
                                label: WHATSAPP_CONVERSATION_ACCESS.NOMBRE_PLURAL,
                                icon: "pi pi-fw pi-comments",
                                routerLink: [
                                    `${menuRouterLink}/whatsapp-inbox`,
                                ],
                            });
                        }
                        break;
                    default:
                        break;
                }
            }
        }
        return menu;
    }

    async getMenuContabilidad(accesoTotal: boolean) {
        const menuRouterLink = `${this.routerLinkBase}/contabilidad`;
        const menu = {
            label: ETIQUETAS.CONTABILIDAD,
            icon: "pi pi-fw pi-book",
            routerLink: [menuRouterLink],
            codigo: "contabilidad",
            items: [],
        };
        if (accesoTotal) {
            menu.items = [
                {
                    label: ETIQUETAS.CUENTAS,
                    icon: "pi pi-fw pi-list",
                    routerLink: [`${menuRouterLink}/cuentas`],
                },
                {
                    label: ETIQUETAS.ASIENTOS,
                    icon: "pi pi-fw pi-pencil",
                    routerLink: [`${menuRouterLink}/asientos`],
                },
            ];
        } else {
            for (const registro of this.grupoAcceso.registros.filter(
                (reg) => reg.modulo === "contabilidad"
            )) {
                switch (registro.codigo) {
                    case "cuenta":
                        if (registro.ver) {
                            menu.items.push({
                                label: ETIQUETAS.CUENTAS,
                                icon: "pi pi-fw pi-list",
                                routerLink: [`${menuRouterLink}/cuentas`],
                            });
                        }
                        break;
                    case "asiento":
                        if (registro.ver) {
                            menu.items.push({
                                label: ETIQUETAS.ASIENTOS,
                                icon: "pi pi-fw pi-pencil",
                                routerLink: [`${menuRouterLink}/asientos`],
                            });
                        }
                        break;
                    default:
                        break;
                }
            }
        }

        return menu;
    }
    async getMenuTesoreria(accesoTotal: boolean) {
        const menuRouterLink = `${this.routerLinkBase}/tesoreria`;
        const menu = {
            label: ETIQUETAS.TESORERIA,
            icon: "pi pi-fw pi-money-bill",
            routerLink: [menuRouterLink],
            codigo: "tesoreria",
            items: [],
        };
        if (accesoTotal) {
            menu.items.push({
                label: ETIQUETAS.REPORTES,
                icon: "pi pi-fw pi-book",
                items: [
                    {
                        label: ETIQUETAS.INFORME_DE_FACTURACION,
                        icon: "pi pi-fw pi-chart-bar",
                        routerLink: [
                            `${menuRouterLink}/reportes/informe-facturacion`,
                        ],
                    },
                    {
                        label: ETIQUETAS.INFORME_DE_RENDICIONES,
                        icon: "pi pi-fw pi-chart-line",
                        routerLink: [
                            `${menuRouterLink}/reportes/listado-rendicion`,
                        ],
                    },
                    {
                        label: ETIQUETAS.INFORME_DE_RENDICIONES_CAJA,
                        icon: "pi pi-fw pi-chart-pie",
                        routerLink: [
                            `${menuRouterLink}/reportes/listado-rendicion-caja`,
                        ],
                    },
                ],
            }),
                menu.items.push({
                    label: ETIQUETAS.PROCESOS,
                    icon: "pi pi-fw pi-cog",
                    items: [
                        {
                            label: ETIQUETAS.CAJERO,
                            icon: "pi pi-fw pi-wallet",
                            routerLink: [`${menuRouterLink}/procesos/cajero`],
                        },
                    ],
                }),
                menu.items.push(
                    ...[
                        {
                            label: ETIQUETAS.CONCEPTOS,
                            icon: "pi pi-fw pi-tags",
                            routerLink: [`${menuRouterLink}/conceptos`],
                        },
                        {
                            label: ETIQUETAS.RENDICIONES,
                            icon: "pi pi-fw pi-inbox",
                            routerLink: [`${menuRouterLink}/rendiciones`],
                        },
                        {
                            label: ETIQUETAS.RENDICIONES_DE_CAJA,
                            icon: "pi pi-fw pi-briefcase",
                            routerLink: [`${menuRouterLink}/rendiciones-caja`],
                        },
                    ]
                );
        } else {
            const reportes = [];
            for (const reporte of this.grupoAcceso.reportes.filter(
                (rep) => rep.modulo === "tesoreria" && rep.activo === true
            )) {
                switch (reporte.codigo) {
                    case "informe_facturacion":
                        reportes.push({
                            label: ETIQUETAS.INFORME_DE_FACTURACION,
                            icon: "pi pi-fw pi-chart-bar",
                            routerLink: [
                                `${menuRouterLink}/reportes/informe-facturacion`,
                            ],
                        });
                        break;
                    case "listado_rendicion":
                        reportes.push({
                            label: ETIQUETAS.INFORME_DE_RENDICIONES,
                            icon: "pi pi-fw pi-chart-line",
                            routerLink: [
                                `${menuRouterLink}/reportes/listado-rendicion`,
                            ],
                        });
                        break;
                    case "listado_rendicion_caja":
                        reportes.push({
                            label: ETIQUETAS.INFORME_DE_RENDICIONES_CAJA,
                            icon: "pi pi-fw pi-chart-pie",
                            routerLink: [
                                `${menuRouterLink}/reportes/listado-rendicion-caja`,
                            ],
                        });
                        break;
                }
            }
            if (reportes.length)
                menu.items.push({
                    label: ETIQUETAS.REPORTES,
                    icon: "pi pi-fw pi-book",
                    items: reportes,
                });
            // PROCESOS
            const procesos = [];
            for (const permiso of this.grupoAcceso.permisos.filter(
                (rep) => rep.activo === true
            )) {
                switch (permiso.codigo) {
                    case "menu_rendicion_caja":
                        procesos.push({
                            label: ETIQUETAS.CAJERO,
                            icon: "pi pi-fw pi-wallet",
                            routerLink: [`${menuRouterLink}/procesos/cajero`],
                        });
                        break;
                }
            }
            if (procesos.length)
                menu.items.push({
                    label: ETIQUETAS.PROCESOS,
                    icon: "pi pi-fw pi-cog",
                    items: procesos,
                });
            for (const registro of this.grupoAcceso.registros.filter(
                (reg) => reg.modulo === "tesoreria"
            )) {
                switch (registro.codigo) {
                    case "concepto":
                        if (registro.ver) {
                            menu.items.push({
                                label: ETIQUETAS.CONCEPTOS,
                                icon: "pi pi-fw pi-tags",
                                routerLink: [`${menuRouterLink}/conceptos`],
                            });
                        }
                        break;
                    case "rendicion":
                        if (registro.ver) {
                            menu.items.push({
                                label: ETIQUETAS.RENDICIONES,
                                icon: "pi pi-fw pi-inbox",
                                routerLink: [`${menuRouterLink}/rendiciones`],
                            });
                        }
                        break;
                    case "rendicion_caja":
                        if (registro.ver) {
                            menu.items.push({
                                label: ETIQUETAS.RENDICIONES_DE_CAJA,
                                icon: "pi pi-fw pi-briefcase",
                                routerLink: [
                                    `${menuRouterLink}/rendiciones-caja`,
                                ],
                            });
                        }
                        break;
                    default:
                        break;
                }
            }
            // const reportes = [];
            // for (const reporte of this.grupoAcceso.reportes.filter( rep => rep.modulo === 'tesoreria' )) {
            //     switch (reporte.codigo) {
            //         case 'listado_rendicion_caja':
            //             if ( reporte.activo ) {
            //                 reportes.push( { label: 'Rendiciones de Caja', icon: this.icon, routerLink: [`${menuRouterLink}/reportes/listado-rendicion-caja`] } )
            //             }
            //     }
            // }
            // if ( reportes.length > 0 ) {
            //     menu.items.push(
            //         {
            //             label: 'Reportes', icon: 'pi pi-fw pi-book',
            //             items: reportes
            //         }
            //     );
            // }
        }

        return menu;
    }

    async getMenuClienteTarjeta(accesoTotal: boolean) {
        const menuRouterLink = `${this.routerLinkBase}/cliente_tarjeta`;
        const menu = {
            label: ETIQUETAS.TARJETA,
            icon: "pi pi-fw pi-credit-card",
            routerLink: [menuRouterLink],
            codigo: "cliente_tarjeta",
            items: [],
        };
        if (accesoTotal) {
            menu.items.push({
                label: ETIQUETAS.REPORTES,
                icon: "pi pi-fw pi-book",
                items: [
                    {
                        label: ETIQUETAS.INFORME_EXTRACTOS_ENVIADOS,
                        icon: "pi pi-fw pi-chart-line",
                        routerLink: [
                            `${menuRouterLink}/reportes/informe-extracto-enviado`,
                        ],
                    },
                    {
                        label: ETIQUETAS.INFORME_DE_TARJETAS,
                        icon: "pi pi-fw pi-chart-bar",
                        routerLink: [
                            `${menuRouterLink}/reportes/informe-tarjeta`,
                        ],
                    },
                    {
                        label: ETIQUETAS.INFORME_CONSULTA_DE_EXTRACTOS,
                        icon: "pi pi-fw pi-search",
                        routerLink: [
                            `${menuRouterLink}/reportes/informe-consulta-extracto`,
                        ],
                    },
                    {
                        label: ETIQUETAS.VERIFICACION_DE_CORREOS,
                        icon: "pi pi-fw pi-check-circle",
                        routerLink: [
                            `${menuRouterLink}/reportes/verificacion-correos`,
                        ],
                    },
                ],
            }),
                menu.items.push({
                    label: ETIQUETAS.PROCESOS,
                    icon: "pi pi-fw pi-cog",
                    items: [
                        {
                            label: ETIQUETAS.ENVIAR_EXTRACTO,
                            icon: "pi pi-fw pi-send",
                            routerLink: [`${menuRouterLink}/procesos/enviar`],
                        },
                        {
                            label: ETIQUETAS.ACTUALIZAR_SALDO_TARJETA,
                            icon: "pi pi-fw pi-refresh",
                            routerLink: [
                                `${menuRouterLink}/procesos/actualizar-saldo-tarjeta`,
                            ],
                        },
                        {
                            label: ETIQUETAS.ACTUALIZAR_SALDO_GRUPO_TARJETA,
                            icon: "pi pi-fw pi-sync",
                            routerLink: [
                                `${menuRouterLink}/procesos/actualizar-saldo-grupo`,
                            ],
                        },
                        {
                            label: ETIQUETAS.IMPORTAR_TARJETAS,
                            icon: "pi pi-fw pi-upload",
                            routerLink: [
                                `${menuRouterLink}/procesos/importar-tarjeta`,
                            ],
                        },
                        {
                            label: ETIQUETAS.ACTUALIZAR_EXTRACTOS,
                            icon: "pi pi-fw pi-replay",
                            routerLink: [
                                `${menuRouterLink}/procesos/actualizar-extracto`,
                            ],
                        },
                        {
                            label: ETIQUETAS.CONSULTA_DE_EXTRACTOS,
                            icon: "pi pi-fw pi-search-plus",
                            routerLink: [
                                `${menuRouterLink}/procesos/consulta-extractos`,
                            ],
                        },
                    ],
                }),
                menu.items.push(
                    ...[
                        {
                            label: ETIQUETAS.GRUPOS_DE_TARJETAS,
                            icon: "pi pi-fw pi-th-large",
                            routerLink: [`${menuRouterLink}/grupo-tarjetas`],
                        },
                        {
                            label: ETIQUETAS.TARJETAS_DE_CREDITO,
                            icon: "pi pi-fw pi-credit-card",
                            routerLink: [`${menuRouterLink}/tarjetas-credito`],
                        },
                        {
                            label: ETIQUETAS.TARJETAS_PREPAGA,
                            icon: "pi pi-fw pi-wallet",
                            routerLink: [`${menuRouterLink}/tarjetas-prepaga`],
                        },
                        {
                            label: ETIQUETAS.EXTRACTOS_ENVIADOS,
                            icon: "pi pi-fw pi-inbox",
                            routerLink: [
                                `${menuRouterLink}/extractos-enviados`,
                            ],
                        },
                    ]
                );
        } else {
            const reportes = [];
            for (const reporte of this.grupoAcceso.reportes.filter(
                (rep) => rep.modulo === "cliente_tarjeta" && rep.activo === true
            )) {
                switch (reporte.codigo) {
                    case "informe_extracto_enviado":
                        reportes.push({
                            label: ETIQUETAS.INFORME_EXTRACTOS_ENVIADOS,
                            icon: "pi pi-fw pi-chart-line",
                            routerLink: [
                                `${menuRouterLink}/reportes/informe-extracto-enviado`,
                            ],
                        });
                        break;
                    case "informe_tarjeta":
                        reportes.push({
                            label: ETIQUETAS.INFORME_DE_TARJETAS,
                            icon: "pi pi-fw pi-chart-bar",
                            routerLink: [
                                `${menuRouterLink}/reportes/informe-tarjeta`,
                            ],
                        });
                        break;
                    case "informe_consulta_extracto":
                        reportes.push({
                            label: ETIQUETAS.INFORME_CONSULTA_DE_EXTRACTOS,
                            icon: "pi pi-fw pi-search",
                            routerLink: [
                                `${menuRouterLink}/reportes/informe-consulta-extracto`,
                            ],
                        });
                        break;
                    case "verificacion_correos":
                        reportes.push({
                            label: ETIQUETAS.VERIFICACION_DE_CORREOS,
                            icon: "pi pi-fw pi-check-circle",
                            routerLink: [
                                `${menuRouterLink}/reportes/verificacion-correos`,
                            ],
                        });
                        break;
                }
            }
            if (reportes.length)
                menu.items.push({
                    label: ETIQUETAS.REPORTES,
                    icon: "pi pi-fw pi-book",
                    items: reportes,
                });
            // PROCESOS
            const procesos = [];
            for (const permiso of this.grupoAcceso.permisos.filter(
                (rep) => rep.activo === true
            )) {
                switch (permiso.codigo) {
                    case "enviar_extracto":
                        procesos.push({
                            label: ETIQUETAS.ENVIAR_EXTRACTO,
                            icon: "pi pi-fw pi-send",
                            routerLink: [`${menuRouterLink}/procesos/enviar`],
                        });
                        break;
                    case "actualizar_saldo_tarjeta":
                        procesos.push({
                            label: ETIQUETAS.ACTUALIZAR_SALDO_TARJETA,
                            icon: "pi pi-fw pi-refresh",
                            routerLink: [
                                `${menuRouterLink}/procesos/actualizar-saldo-tarjeta`,
                            ],
                        });
                        break;
                    case "actualizar_saldo_grupo":
                        procesos.push({
                            label: ETIQUETAS.ACTUALIZAR_SALDO_GRUPO_TARJETA,
                            icon: "pi pi-fw pi-sync",
                            routerLink: [
                                `${menuRouterLink}/procesos/actualizar-saldo-grupo`,
                            ],
                        });
                        break;
                    case "importar_tarjeta":
                        procesos.push({
                            label: ETIQUETAS.IMPORTAR_TARJETAS,
                            icon: "pi pi-fw pi-upload",
                            routerLink: [
                                `${menuRouterLink}/procesos/importar-tarjeta`,
                            ],
                        });
                        break;
                    case "actualizar_extracto":
                        procesos.push({
                            label: ETIQUETAS.ACTUALIZAR_EXTRACTOS,
                            icon: "pi pi-fw pi-replay",
                            routerLink: [
                                `${menuRouterLink}/procesos/actualizar-extracto`,
                            ],
                        });
                        break;
                    case "consulta_extractos":
                        procesos.push({
                            label: ETIQUETAS.CONSULTA_DE_EXTRACTOS,
                            icon: "pi pi-fw pi-search-plus",
                            routerLink: [
                                `${menuRouterLink}/procesos/consulta-extractos`,
                            ],
                        });
                        break;
                }
            }
            if (procesos.length)
                menu.items.push({
                    label: ETIQUETAS.PROCESOS,
                    icon: "pi pi-fw pi-cog",
                    items: procesos,
                });

            for (const registro of this.grupoAcceso.registros.filter(
                (reg) => reg.modulo === "cliente_tarjeta"
            )) {
                switch (registro.codigo) {
                    case "grupo_tarjeta":
                        if (registro.ver) {
                            menu.items.push({
                                label: ETIQUETAS.GRUPOS_DE_TARJETAS,
                                icon: "pi pi-fw pi-th-large",
                                routerLink: [
                                    `${menuRouterLink}/grupo-tarjetas`,
                                ],
                            });
                        }
                        break;
                    case "tarjeta_credito":
                        if (registro.ver) {
                            menu.items.push({
                                label: ETIQUETAS.TARJETAS_DE_CREDITO,
                                icon: "pi pi-fw pi-credit-card",
                                routerLink: [
                                    `${menuRouterLink}/tarjetas-credito`,
                                ],
                            });
                        }
                        break;
                    case "tarjeta_prepaga":
                        if (registro.ver) {
                            menu.items.push({
                                label: ETIQUETAS.TARJETAS_PREPAGA,
                                icon: "pi pi-fw pi-wallet",
                                routerLink: [
                                    `${menuRouterLink}/tarjetas-prepaga`,
                                ],
                            });
                        }
                        break;
                    case "extracto_enviado":
                        if (registro.ver) {
                            menu.items.push({
                                label: ETIQUETAS.EXTRACTOS_ENVIADOS,
                                icon: "pi pi-fw pi-inbox",
                                routerLink: [
                                    `${menuRouterLink}/extractos-enviados`,
                                ],
                            });
                        }
                        break;
                    default:
                        break;
                }
            }
        }

        return menu;
    }

    async getMenuPrestamoSolidario(accesoTotal: boolean) {
        const menuRouterLink = `${this.routerLinkBase}/prestamo_solidario`;
        const menu = {
            label: ETIQUETAS.PRESTAMO_SOLIDARIO,
            icon: "pi pi-fw pi-credit-card",
            routerLink: [menuRouterLink],
            codigo: "prestamo_solidario",
            items: [],
        };
        if (accesoTotal) {
            menu.items.push({
                label: ETIQUETAS.REPORTES,
                icon: "pi pi-fw pi-book",
                items: [
                    {
                        label: ETIQUETAS.INFORME_PRESTAMOS,
                        icon: "pi pi-fw pi-chart-bar",
                        routerLink: [
                            `${menuRouterLink}/reportes/informe-prestamo`,
                        ],
                    },
                    {
                        label: ETIQUETAS.SEGUIMIENTO_DE_PRESTAMOS,
                        icon: "pi pi-fw pi-chart-line",
                        routerLink: [
                            `${menuRouterLink}/reportes/seguimiento-prestamo`,
                        ],
                    },
                ],
            });
            menu.items.push(
                ...[
                    {
                        label: ETIQUETAS.PRESTAMOS,
                        icon: "pi pi-fw pi-users",
                        routerLink: [`${menuRouterLink}/prestamos`],
                    },
                ]
            );
        } else {
            const reportes = [];
            for (const reporte of this.grupoAcceso.reportes.filter(
                (rep) =>
                    rep.modulo === "prestamo_solidario" && rep.activo === true
            )) {
                switch (reporte.codigo) {
                    case "informe_prestamo":
                        reportes.push({
                            label: ETIQUETAS.INFORME_PRESTAMOS,
                            icon: "pi pi-fw pi-chart-bar",
                            routerLink: [
                                `${menuRouterLink}/reportes/informe-prestamo`,
                            ],
                        });
                        break;
                    case "seguimiento_prestamo":
                        reportes.push({
                            label: ETIQUETAS.SEGUIMIENTO_DE_PRESTAMOS,
                            icon: "pi pi-fw pi-chart-line",
                            routerLink: [
                                `${menuRouterLink}/reportes/seguimiento-prestamo`,
                            ],
                        });
                        break;
                }
            }
            if (reportes.length)
                menu.items.push({
                    label: ETIQUETAS.REPORTES,
                    icon: "pi pi-fw pi-book",
                    items: reportes,
                });
            for (const registro of this.grupoAcceso.registros.filter(
                (reg) => reg.modulo === "prestamo_solidario"
            )) {
                switch (registro.codigo) {
                    case "prestamo":
                        if (registro.ver) {
                            menu.items.push({
                                label: ETIQUETAS.PRESTAMOS,
                                icon: "pi pi-fw pi-users",
                                routerLink: [`${menuRouterLink}/prestamos`],
                            });
                        }
                        break;
                    default:
                        break;
                }
            }
        }

        return menu;
    }

    async getMenuPrestamo(accesoTotal: boolean) {
        const menuRouterLink = `${this.routerLinkBase}/prestamo`;
        const menu = {
            label: ETIQUETAS.PRESTAMO,
            icon: "pi pi-fw pi-dollar",
            routerLink: [menuRouterLink],
            codigo: "prestamo",
            items: [],
        };
        if (accesoTotal) {
            menu.items.push({
                label: ETIQUETAS.REPORTES,
                icon: "pi pi-fw pi-book",
                items: [
                    {
                        label: ETIQUETAS.INFORME_DE_PAGARES,
                        icon: "pi pi-fw pi-chart-bar",
                        routerLink: [
                            `${menuRouterLink}/reportes/informe-pagare`,
                        ],
                    },
                ],
            });
            menu.items.push({
                label: ETIQUETAS.PROCESOS,
                icon: "pi pi-fw pi-cog",
                items: [
                    {
                        label: ETIQUETAS.PROCESAR_PAGARES,
                        icon: "pi pi-fw pi-play",
                        routerLink: [
                            `${menuRouterLink}/procesos/procesar-pagare`,
                        ],
                    },
                ],
            }),
                menu.items.push(
                    ...[
                        {
                            label: ETIQUETAS.PAGARES,
                            icon: "pi pi-fw pi-file",
                            routerLink: [`${menuRouterLink}/pagares`],
                        },
                        {
                            label: ETIQUETAS.ESTADO_DE_PAGARES,
                            icon: "pi pi-fw pi-flag",
                            routerLink: [`${menuRouterLink}/estado-pagares`],
                        },
                    ]
                );
        } else {
            const reportes = [];
            for (const reporte of this.grupoAcceso.reportes.filter(
                (rep) => rep.modulo === "prestamo" && rep.activo === true
            )) {
                switch (reporte.codigo) {
                    case "informe_pagare":
                        reportes.push({
                            label: ETIQUETAS.INFORME_DE_PAGARES,
                            icon: "pi pi-fw pi-chart-bar",
                            routerLink: [
                                `${menuRouterLink}/reportes/informe-pagare`,
                            ],
                        });
                        break;
                }
            }
            if (reportes.length)
                menu.items.push({
                    label: ETIQUETAS.REPORTES,
                    icon: "pi pi-fw pi-book",
                    items: reportes,
                });
            //procesos
            const procesos = [];
            for (const permiso of this.grupoAcceso.permisos.filter(
                (rep) => rep.activo === true
            )) {
                switch (permiso.codigo) {
                    case "procesar_pagare":
                        procesos.push({
                            label: ETIQUETAS.PROCESAR_PAGARES,
                            icon: "pi pi-fw pi-play",
                            routerLink: [
                                `${menuRouterLink}/procesos/procesar-pagare`,
                            ],
                        });
                        break;
                }
            }
            if (procesos.length)
                menu.items.push({
                    label: ETIQUETAS.PROCESOS,
                    icon: "pi pi-fw pi-cog",
                    items: procesos,
                });
            for (const registro of this.grupoAcceso.registros.filter(
                (reg) => reg.modulo === "prestamo"
            )) {
                switch (registro.codigo) {
                    case "pagare":
                        if (registro.ver) {
                            menu.items.push({
                                label: ETIQUETAS.PAGARES,
                                icon: "pi pi-fw pi-file",
                                routerLink: [`${menuRouterLink}/pagares`],
                            });
                        }
                        break;
                    case "estado_pagare":
                        if (registro.ver) {
                            menu.items.push({
                                label: ETIQUETAS.ESTADO_DE_PAGARES,
                                icon: "pi pi-fw pi-flag",
                                routerLink: [
                                    `${menuRouterLink}/estado-pagares`,
                                ],
                            });
                        }
                        break;
                    default:
                        break;
                }
            }
        }

        return menu;
    }

    async getMenuCRM(accesoTotal: boolean) {
        const menuRouterLink = `${this.routerLinkBase}/crm`;
        const menu = {
            label: ETIQUETAS.CRM,
            icon: "pi pi-fw pi-desktop",
            routerLink: [menuRouterLink],
            codigo: "crm",
            items: [],
        };
        if (accesoTotal) {
            menu.items.push({
                label: ETIQUETAS.REPORTES,
                icon: "pi pi-fw pi-book",
                items: [
                    {
                        label: ETIQUETAS.GESTION_DE_CARTERA,
                        icon: "pi pi-fw pi-briefcase",
                        routerLink: [
                            `${menuRouterLink}/reportes/gestion-cartera`,
                        ],
                    },
                ],
            });
        } else {
            const reportes = [];
            for (const reporte of this.grupoAcceso.reportes.filter(
                (rep) => rep.modulo === "notificacion" && rep.activo === true
            )) {
                switch (reporte.codigo) {
                    case "gestion_cartera":
                        reportes.push({
                            label: ETIQUETAS.GESTION_DE_CARTERA,
                            icon: "pi pi-fw pi-briefcase",
                            routerLink: [
                                `${menuRouterLink}/reportes/gestion-cartera`,
                            ],
                        });
                        break;
                }
            }
            if (reportes.length)
                menu.items.push({
                    label: ETIQUETAS.REPORTES,
                    icon: "pi pi-fw pi-book",
                    items: reportes,
                });

            for (const registro of this.grupoAcceso.registros.filter(
                (reg) => reg.modulo === "crm"
            )) {
                switch (registro.codigo) {
                    // case 'contacto':
                    //     if ( registro.ver ) {
                    //         menu.items.push( {label: 'Contactos', icon: this.icon, routerLink: [`${menuRouterLink}/contactos`]} )
                    //     }
                    // break;
                    default:
                        break;
                }
            }
        }
        return menu;
    }

    async getMenuSeguimiento(accesoTotal: boolean) {
        const menuRouterLink = `${this.routerLinkBase}/seguimiento`;
        const menu = {
            label: ETIQUETAS.SEGUIMIENTO,
            icon: "pi pi-eye",
            routerLink: [menuRouterLink],
            codigo: "seguimiento",
            items: [],
        };
        if (accesoTotal) {
            menu.items.push({
                label: ETIQUETAS.REPORTES,
                icon: "pi pi-fw pi-book",
                items: [
                    {
                        label: ETIQUETAS.INFORME_DE_LLAMADAS,
                        icon: "pi pi-fw pi-phone",
                        routerLink: [
                            `${menuRouterLink}/reportes/informe-llamadas`,
                        ],
                    },
                    {
                        label: ETIQUETAS.GESTION_COBROS,
                        icon: "pi pi-fw pi-dollar",
                        routerLink: [
                            `${menuRouterLink}/reportes/gestion-cobros`,
                        ],
                    },
                    {
                        label: ETIQUETAS.COBROS_POR_RECUPERADOR,
                        icon: "pi pi-fw pi-user-edit",
                        routerLink: [
                            `${menuRouterLink}/reportes/cobros-recuperador`,
                        ],
                    },
                    {
                        label: ETIQUETAS.CONSULTA_DE_TARJETAS_DE_CREDITO,
                        icon: "pi pi-fw pi-credit-card",
                        routerLink: [
                            `${menuRouterLink}/reportes/tarjetas-credito-actualizadas`,
                        ],
                    },
                ],
            });
        } else {
            const reportes = [];
            for (const reporte of this.grupoAcceso.reportes.filter(
                (rep) => rep.modulo === "seguimiento" && rep.activo === true
            )) {
                switch (reporte.codigo) {
                    case "llamadas":
                        reportes.push({
                            label: ETIQUETAS.INFORME_DE_LLAMADAS,
                            icon: "pi pi-fw pi-phone",
                            routerLink: [
                                `${menuRouterLink}/reportes/informe-llamadas`,
                            ],
                        });
                        break;
                    case "gestion-cobros":
                        reportes.push({
                            label: ETIQUETAS.GESTION_COBROS,
                            icon: "pi pi-fw pi-dollar",
                            routerLink: [
                                `${menuRouterLink}/reportes/gestion-cobros`,
                            ],
                        });
                        break;
                    case "cobros-recuperador":
                        reportes.push({
                            label: ETIQUETAS.COBROS_POR_RECUPERADOR,
                            icon: "pi pi-fw pi-user-edit",
                            routerLink: [
                                `${menuRouterLink}/reportes/cobros-recuperador`,
                            ],
                        });
                        break;
                    case "tarjetas-credito-actualizadas":
                        reportes.push({
                            label: ETIQUETAS.CONSULTA_DE_TARJETAS_DE_CREDITO,
                            icon: "pi pi-fw pi-credit-card",
                            routerLink: [
                                `${menuRouterLink}/reportes/tarjetas-credito-actualizadas`,
                            ],
                        });
                        break;
                }
            }
            if (reportes.length)
                menu.items.push({
                    label: ETIQUETAS.REPORTES,
                    icon: "pi pi-fw pi-book",
                    items: reportes,
                });
            // for (const registro of this.grupoAcceso.registros.filter( reg => reg.modulo === 'seguimiento') ) {
            //     switch (registro.codigo) {
            //         case 'llamadas':
            //             if ( registro.ver ) {
            //                 menu.items.push( {label: 'Llamadas', icon: this.icon, routerLink: [`${menuRouterLink}/llamadas`]} )
            //             }
            //         break;
            //         case 'gestion-cobros':
            //             if( registro.ver ){
            //                 menu.items.push( {label: 'Gestion Cobros', icon: this.icon, routerLink: [`${menuRouterLink}/gestion-cobros`]} )
            //             }
            //         break;
            //         case 'cobros-recuperador':
            //             if( registro.ver ){
            //                 menu.items.push( {label: 'Cobros de Recuperador', icon: this.icon, routerLink: [`${menuRouterLink}/cobros-recuperador`]} )
            //             }
            //         break;
            //         case 'tarjetas-credito-actualizadas':
            //             if( registro.ver ){
            //                 menu.items.push( {label: 'Tarjetas de credito actualizadas', icon: this.icon, routerLink: [`${menuRouterLink}/tarjetas-credito-actualizadas`]} )
            //             }
            //         break;
            //         default:
            //             break;
            //     }
            // }
        }
        return menu;
    }

    async getMenuAsociaciones(accesoTotal: boolean) {
        const menuRouterLink = `${this.routerLinkBase}/asociaciones`;
        const menu = {
            label: ETIQUETAS.ASOCIACIONES,
            icon: "pi pi-fw pi-money-bill",
            routerLink: [menuRouterLink],
            codigo: "asociaciones",
            items: [],
        };
        if (accesoTotal) {
            menu.items.push({
                label: ETIQUETAS.REPORTES,
                icon: "pi pi-fw pi-book",
                items: [
                    {
                        label: ETIQUETAS.ESTADO_DE_CUENTA,
                        icon: "pi pi-fw pi-file",
                        routerLink: [
                            `${menuRouterLink}/reportes/estado-cuenta`,
                        ],
                    },
                    // { label: 'Informe de Rendiciones ', icon: this.icon, routerLink: [`${menuRouterLink}/reportes/listado-rendicion`] },
                    // { label: 'Informe de Rendiciones Caja ', icon: this.icon, routerLink: [`${menuRouterLink}/reportes/listado-rendicion-caja`] }
                ],
            }),
                menu.items.push({
                    label: ETIQUETAS.PROCESOS,
                    icon: "pi pi-fw pi-cog",
                    items: [
                        {
                            label: ETIQUETAS.SOLICITUD_DE_CREDITO,
                            icon: "pi pi-fw pi-external-link",
                            url: `https://pagofacil.com.py/solicitud/#/auth/login?id=${encodeURIComponent(
                                this.linkPagoFacilSolicitudes
                            )}`,
                        },
                    ],
                });
            // menu.items.push(...[
            //     {label: 'Conceptos', icon: this.icon, routerLink: [`${menuRouterLink}/conceptos`]},
            //     {label: 'Rendiciones', icon: this.icon, routerLink: [`${menuRouterLink}/rendiciones`]},
            //     {label: 'Rendiciones de Caja', icon: this.icon, routerLink: [`${menuRouterLink}/rendiciones-caja`]}
            // ]);
        } else {
            const reportes = [];
            for (const reporte of this.grupoAcceso.reportes.filter(
                (rep) => rep.modulo === "asociaciones" && rep.activo === true
            )) {
                switch (reporte.codigo) {
                    case "estado_cuenta":
                        reportes.push({
                            label: ETIQUETAS.ESTADO_DE_CUENTA,
                            icon: "pi pi-fw pi-file",
                            routerLink: [
                                `${menuRouterLink}/reportes/estado-cuenta`,
                            ],
                        });
                        break;
                    // case 'listado_rendicion':
                    //     reportes.push( { label: 'Informe de Rendiciones ', icon: this.icon, routerLink: [`${menuRouterLink}/reportes/listado-rendicion`] } );
                    //     break;
                    // case 'listado_rendicion_caja':
                    //     reportes.push( { label: 'Informe de Rendiciones Caja ', icon: this.icon, routerLink: [`${menuRouterLink}/reportes/listado-rendicion-caja`] } );
                    //     break;
                }
            }
            if (reportes.length)
                menu.items.push({
                    label: ETIQUETAS.REPORTES,
                    icon: "pi pi-fw pi-book",
                    items: reportes,
                });
            // PROCESOS
            const procesos = [];
            for (const permiso of this.grupoAcceso.permisos.filter(
                (rep) => rep.activo === true
            )) {
                switch (permiso.codigo) {
                    case "solicitud_credito":
                        procesos.push({
                            label: ETIQUETAS.SOLICITUD_DE_CREDITO,
                            icon: "pi pi-fw pi-external-link",
                            url: `https://pagofacil.com.py/solicitud/${this.linkPagoFacilSolicitudes}`,
                            target: "_blank",
                        });
                        break;
                }
            }
            if (procesos.length)
                menu.items.push({
                    label: ETIQUETAS.PROCESOS,
                    icon: "pi pi-fw pi-cog",
                    items: procesos,
                });
            // for (const permiso of this.grupoAcceso.permisos.filter( rep => (rep.activo === true) ) ) {
            //     switch (permiso.codigo) {
            //         case 'solicitud_credito':
            //             procesos.push( { label: 'Solicitud de Credito ',  icon: this.icon, url: 'https://pagofacil.com.py/solicitud', target: '_blank' } );
            //             break;
            //     }
            // }
            // if ( procesos.length ) menu.items.push( { label: 'Procesos', icon: 'pi pi-fw pi-cog', items: procesos } );
            for (const registro of this.grupoAcceso.registros.filter(
                (reg) => reg.modulo === "asociaciones"
            )) {
                switch (registro.codigo) {
                    // case 'solicitud-credito':
                    //     if ( registro.ver ) {
                    //         menu.items.push({ label: 'Solicitud de Credito', icon: this.icon, url: 'https://pagofacil.com.py/solicitud', target: '_blank' })
                    //     }
                    // break;
                    // case 'rendicion':
                    //     if ( registro.ver ) {
                    //         menu.items.push( {label: 'Rendiciones', icon: this.icon, routerLink: [`${menuRouterLink}/rendiciones`]} )
                    //     }
                    // break;
                    // case 'rendicion_caja':
                    //     if ( registro.ver ) {
                    //         menu.items.push( {label: 'Rendiciones de Caja', icon: this.icon, routerLink: [`${menuRouterLink}/rendiciones-caja`]} )
                    //     }
                    // break;
                    default:
                        break;
                }
            }
        }
        return menu;
    }
}
